// DespachoContainer.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import { CardComponent } from '../../../components/Layouts/Card.styles';
import { Stack, TextInput, Checkbox, CheckboxGroup, NumberInput, Button, InlineLoading } from '@carbon/react';
import Typography from '../../../components/Basics/Typography';
import LoadingContent from '../../../components/Animations/LoadingContent';
import { onGetDespachoThunk, onGetProductionTargetsThunk, onSaveProductionTargetsThunk } from './Despacho.actions';
import { onGetRealTimeThunk } from '../Performance/Performance.actions';
import BarChartObj from '../../../components/Graphs/BarChartObj.container';
import RouteRow from '../../../components/Layouts/TravelCard';
import { SideBySideContainer, Row, Column, Cell, RouteSummary, CardGrid, CustomTag, TagsRowContainer, FlexWrapContainer, SectionTitle } from './Despacho.styles';
import { ChartMultitype, Settings, Save } from '@carbon/icons-react';
import Card from '../../../components/Layouts/Card';
import { useInput } from '../../../components/Inputs/Inputs.hooks';
import { green20, gray10 } from '@carbon/colors';
import { g100 } from "@carbon/themes";
import {
  magenta70,
  cyan70,
  teal70,
  purple70,
  green70,
  coolGray70,
} from '@carbon/colors';

const DespachoContainer = () => {
  const dispatch = useDispatch();
  const { 
    error, 
    loading, 
    fromPlatforms = [], 
    toPlatforms = [], 
    geofenceGroups = {}, 
    unassignedTrucks = [],
    excludedTrucks = [],
    targets: savedTargets = {},
    targetsLoading,
    targetsSaving
  } = useSelector(s => s.despacho);
  
  const { m3Production } = useSelector(state => state.performance);
  const barChart = m3Production;

  const axes = {
    "left": {
      "mapsTo": "key",
      "scaleType": "labels"
    },
    "bottom": {
      "mapsTo": "value"
    }
  };

  const { value: searchValue, onChange: handleSearch } = useInput({
    initialValue: ''
  });
  const { value: searchValueMissing, onChange: handleSearchMissing } = useInput({
    initialValue: ''
  });
  const { value: searchValueExcluded, onChange: handleSearchExcluded } = useInput({
    initialValue: ''
  });

  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [showTargetInputs, setShowTargetInputs] = useState(false);
  const [targets, setTargets] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Cargar datos iniciales
  useEffect(() => {
    batch(() => {
      dispatch(onGetDespachoThunk());
      dispatch(onGetRealTimeThunk());
      dispatch(onGetProductionTargetsThunk());
    });

    const realTimeInterval = setInterval(() => {
      dispatch(onGetRealTimeThunk());
    }, 60000);

    const despachoInterval = setInterval(() => {
      dispatch(onGetDespachoThunk());
    }, 120000);

    return () => {
      clearInterval(realTimeInterval);
      clearInterval(despachoInterval);
    };
  }, [dispatch]);

  // Sincronizar targets del Redux con el estado local
  useEffect(() => {
    if (Object.keys(savedTargets).length > 0) {
      setTargets(savedTargets);
      setHasUnsavedChanges(false);
    }
  }, [savedTargets]);

  // Inicializar targets con valores por defecto cuando cambia barChart
  useEffect(() => {
    if (barChart && barChart.length > 0 && Object.keys(targets).length === 0) {
      const initialTargets = {};
      barChart.forEach(item => {
        initialTargets[item.key] = savedTargets[item.key] || 0;
      });
      setTargets(initialTargets);
    }
  }, [barChart, savedTargets]);

  // Manejar cambio de valor objetivo
  const handleTargetChange = (key, value) => {
    setTargets(prev => ({
      ...prev,
      [key]: parseFloat(value) || 0
    }));
    setHasUnsavedChanges(true);
  };

  // Guardar objetivos
  const handleSaveTargets = async () => {
    try {
      await dispatch(onSaveProductionTargetsThunk(targets));
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error guardando objetivos:', error);
    }
  };

  const pairs = useMemo(() => {
    const to = Array.isArray(toPlatforms) ? toPlatforms : [];
    const from = Array.isArray(fromPlatforms) ? fromPlatforms : [];
    const keyOf = (r) => `${r.origin} → ${r.destination}`;
    const emptyLike = (r) => ({ origin: r.origin, destination: r.destination, trucks: [] });
    const map = new Map();

    to.forEach((r) => {
      const k = keyOf(r);
      map.set(k, { ida: r, vuelta: null });
    });

    from.forEach((r) => {
      const k = keyOf(r);
      const cur = map.get(k) || { ida: null, vuelta: r };
      cur.vuelta = r;
      map.set(k, cur);
    });

    const orderedKeys = [
      ...to.map(keyOf),
      ...from.map(keyOf).filter((k) => !to.some((r) => keyOf(r) === k)),
    ];

    return orderedKeys.map((k) => {
      const { ida, vuelta } = map.get(k) || { ida: null, vuelta: null };
      return {
        key: k,
        ida: ida || (vuelta ? emptyLike(vuelta) : null),
        vuelta: vuelta || (ida ? emptyLike(ida) : null),
      };
    });
  }, [toPlatforms, fromPlatforms]);

  const routeOptions = useMemo(() => {
    const origins = pairs.map(p => p.ida?.origin || p.vuelta?.origin).filter(Boolean);
    return [...new Set(origins)];
  }, [pairs]);

  const handleRouteCheckbox = (evt) => {
    const { value, checked } = evt.target;
    setSelectedRoutes((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      if (checked) {
        if (arr.includes(value)) return arr;
        return [...arr, value];
      }
      return arr.filter((v) => v !== value);
    });
  };

  const filteredUnassignedTrucks = useMemo(() => {
    if (!searchValueMissing.trim()) return unassignedTrucks;
    const normalized = searchValueMissing.toLowerCase();
    return unassignedTrucks.filter(truck => {
      const patent = (truck.patent || '').toLowerCase();
      const equipo = (truck.equipo || '').toLowerCase();
      return patent.includes(normalized) || equipo.includes(normalized);
    });
  }, [unassignedTrucks, searchValueMissing]);

  const filteredExcludedTrucks = useMemo(() => {
    if (!searchValueExcluded.trim()) return excludedTrucks;
    const normalized = searchValueExcluded.toLowerCase();
    return excludedTrucks.filter(truck => {
      const patent = (truck.patent || '').toLowerCase();
      const equipo = (truck.equipo || '').toLowerCase();
      return patent.includes(normalized) || equipo.includes(normalized);
    });
  }, [excludedTrucks, searchValueExcluded]);

  const STATUS_CONFIG = [
    { label: 'EQUIPOS DETENIDOS APAGADOS (PROLONGADO)', color: magenta70 },
    { label: 'EQUIPOS APAGADOS (RECIENTEMENTE)', color: coolGray70 },
    { label: 'DETENIDOS', color: cyan70 },
    { label: 'EN MANTENIMIENTO', color: purple70 },
    { label: 'EN COLACIÓN', color: teal70 },
    { label: 'OPERATIVOS', color: green70 },
  ];

  const filteredPairs = useMemo(() => {
    let filtered = pairs;

    if (searchValue.trim()) {
      const normalized = searchValue.toLowerCase();
      filtered = filtered.filter(({ ida, vuelta }) => {
        const matchInTrucks = (route) => {
          if (!route?.trucks || !Array.isArray(route.trucks)) return false;
          return route.trucks.some((truck) =>
            (truck.name || truck.patent || '').toLowerCase().includes(normalized)
          );
        };
        return matchInTrucks(ida) || matchInTrucks(vuelta);
      });
    }

    if (selectedRoutes.length > 0) {
      filtered = filtered.filter(({ ida, vuelta }) => {
        const origin = ida?.origin || vuelta?.origin;
        return selectedRoutes.includes(origin);
      });
    }

    return filtered;
  }, [pairs, searchValue, selectedRoutes]);

  return (
    <>
      {loading && !error && <LoadingContent />}
      {!loading && error && (
        <CardComponent width="10%">
          <Typography className="cds--type-heading-compact-01">
            Error al buscar data tiempo real. Estamos solucionado el problema
          </Typography>
        </CardComponent>
      )}
      {!loading && !error && (
        <>
          <CardComponent width="100%" style={{ marginBottom: '20px' }}>
            <Card justifyContent="space-between" alignItems="center">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ChartMultitype size={18} />
                <Typography className="cds--type-heading-compact-01">
                  PRODUCCIÓN POR FRENTE DE DESCARGA
                </Typography>
              </div>
              <button
                onClick={() => setShowTargetInputs(!showTargetInputs)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: gray10
                }}
              >
                <Settings size={18} />
                <Typography className="cds--type-body-compact-01">
                  {showTargetInputs ? 'Ocultar objetivos' : 'Configurar objetivos'}
                </Typography>
              </button>
            </Card>

            {showTargetInputs && (
              <Card background='unset' padding='12px' flexDirection='column' gap='12px'>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <Typography className="cds--type-body-compact-01">
                    Define los valores objetivo (m³) para cada frente:
                  </Typography>
                  {targetsSaving ? (
                    <InlineLoading description="Guardando..." />
                  ) : (
                    <Button
                      kind="primary"
                      size="sm"
                      renderIcon={Save}
                      onClick={handleSaveTargets}
                      disabled={!hasUnsavedChanges}
                    >
                      Guardar objetivos
                    </Button>
                  )}
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                  {barChart?.map((item) => (
                    <NumberInput
                      key={item.key}
                      id={`target-${item.key}`}
                      label={item.key}
                      value={targets[item.key] || 0}
                      onChange={(e, { value }) => handleTargetChange(item.key, value)}
                      min={0}
                      step={100}
                      hideSteppers={false}
                    />
                  ))}
                </div>
                
                {hasUnsavedChanges && (
                  <Typography className='cds--type-helper-text-01' style={{ color: '#fa4d56' }}>
                    Tienes cambios sin guardar
                  </Typography>
                )}
              </Card>
            )}

            <Card height="430px" width="100%">
              <BarChartObj
                title='Producción por Frente de descarga (total, m³)'
                data={barChart}
                targets={targets}
                axesOptions={axes}
              />
            </Card>

            <Card justifyContent="flex-start">
              <ChartMultitype size={18} />
              <Typography className="cds--type-heading-compact-01">
                EQUIPOS DETENIDOS
              </Typography>
            </Card>

            <Card background='unset' padding='12px' flexDirection='column'>
              <TagsRowContainer>
                {STATUS_CONFIG.map((tag, index) => (
                  <CustomTag key={index} $color={tag.color}>
                    {tag.label}
                  </CustomTag>
                ))}
              </TagsRowContainer>
              <CardGrid>
                {Object.entries(geofenceGroups).map(([geofence, trucks]) => (
                  <Card
                    key={geofence}
                    background='unset'
                    padding='12px'
                    display="flex"
                    flexDirection="column"
                    gap="8px"
                  >
                    <Typography
                      className="cds--type-heading-compact-01"
                      style={{ opacity: 0.8, fontSize: "0.85rem" }}
                    >
                      {geofence}
                    </Typography>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                      {trucks.map((t) => (
                        <CustomTag
                          key={t.patent}
                          $color={t.estadoColor}
                        >
                          {t.patent}
                        </CustomTag>
                      ))}
                    </div>
                  </Card>
                ))}
              </CardGrid>
              <Typography className='cds--type-helper-text-01' color={g100.textHelper}>
                Aquí solo aparecen equipos que se encuentren en las geocercas de punto de interés.
              </Typography>
            </Card>

            <Card justifyContent="flex-start">
              <ChartMultitype size={18} />
              <Typography className="cds--type-heading-compact-01">
                RUTAS
              </Typography>
            </Card>

            <CheckboxGroup
              legendText="Filtrar rutas por origen"
              orientation="horizontal"
            >
              {routeOptions.map((origin) => (
                <Checkbox
                  key={origin}
                  id={`route-${origin}`}
                  labelText={origin}
                  value={origin}
                  onChange={handleRouteCheckbox}
                  checked={selectedRoutes.includes(origin)}
                />
              ))}
            </CheckboxGroup>

            <TextInput
              placeholder="Buscar por equipo"
              id="truck-search"
              onChange={handleSearch}
              value={searchValue}
            />

            <SideBySideContainer>
              <Stack>
                {filteredPairs.map(({ key, ida, vuelta }) => (
                  <React.Fragment key={key}>
                    <Row>
                      <Column>
                        <Cell>{ida && <RouteRow route={ida} />}</Cell>
                      </Column>
                      <Column>
                        <Cell>{vuelta && <RouteRow route={vuelta} />}</Cell>
                      </Column>
                    </Row>
                    <RouteSummary>
                      <ChartMultitype size={16} color={gray10}></ChartMultitype>
                      <Typography className='cds--type-heading-compact-01' color={gray10}>
                        DETALLES DE LA RUTA
                      </Typography>
                      <Typography className='cds--type-heading-compact-01' color={green20}>
                        {(ida?.trucks?.length || 0) + (vuelta?.trucks?.length || 0)} CAMIONES ACTIVOS
                      </Typography>
                    </RouteSummary>
                  </React.Fragment>
                ))}
              </Stack>
            </SideBySideContainer>

            <Card justifyContent="flex-start">
              <ChartMultitype size={18} />
              <Typography className="cds--type-heading-compact-01">
                EQUIPOS SIN ASIGNACIÓN
              </Typography>
            </Card>

            <Card background='unset' padding='12px' flexDirection='column'>
              <TextInput
                placeholder="Buscar por equipo"
                id="truck-search-missing"
                onChange={handleSearchMissing}
                value={searchValueMissing}
              />
              <FlexWrapContainer>
                <SectionTitle>Equipos sin asignación</SectionTitle>
                {filteredUnassignedTrucks.length > 0 ? (
                  filteredUnassignedTrucks.map((truck, index) => (
                    <CustomTag key={index} $color={truck.estadoColor}>
                      {truck.patent}
                    </CustomTag>
                  ))
                ) : (
                  <Typography className='cds--type-helper-text-01' color={g100.textHelper}>
                    {searchValueMissing
                      ? 'No se encontraron equipos con ese criterio'
                      : 'No hay equipos sin asignar'}
                  </Typography>
                )}
              </FlexWrapContainer>
              <Typography className='cds--type-helper-text-01' color={g100.textHelper}>
                Aquí solo aparecen equipos sin asignación que se encuentren fuera de las geocercas de punto de interés.
              </Typography>
            </Card>

            <Card justifyContent="flex-start">
              <ChartMultitype size={18} />
              <Typography className="cds--type-heading-compact-01">
                EQUIPOS INACTIVOS (+30 DÍAS)
              </Typography>
            </Card>

            <Card background='unset' padding='12px' flexDirection='column'>
              <TextInput
                placeholder="Buscar por equipo"
                id="truck-search-excluded"
                onChange={handleSearchExcluded}
                value={searchValueExcluded}
              />
              <FlexWrapContainer>
                <SectionTitle>Equipos sin actividad reciente</SectionTitle>
                {filteredExcludedTrucks.length > 0 ? (
                  filteredExcludedTrucks.map((truck, index) => (
                    <CustomTag key={index} $color={truck.estadoColor}>
                      {truck.patent}
                    </CustomTag>
                  ))
                ) : (
                  <Typography className='cds--type-helper-text-01' color={g100.textHelper}>
                    {searchValueExcluded
                      ? 'No se encontraron equipos con ese criterio'
                      : 'No hay equipos inactivos'}
                  </Typography>
                )}
              </FlexWrapContainer>
              <Typography className='cds--type-helper-text-01' color={g100.textHelper}>
                Aquí solo aparecen equipos que no han enviado datos desde hace 1 mes o más.
              </Typography>
            </Card>
          </CardComponent>
        </>
      )}
    </>
  );
};

export default DespachoContainer;